/** @format */

/* eslint require-await: "warn" */
import { AxiosRequestConfig } from 'axios';
import { pick } from 'lodash';
import pRetry from 'p-retry';

import { NamedException } from '@toucan/client/global/application';
import { client } from '@toucan/client/global/axios';
import { decimalToCent } from '@toucan/client/global/currency';
import { MarketCode } from '@toucan/client/global/market';
import {
    ProductConditionValue,
    productConditionEnumToLegacyEnum,
} from '@toucan/client/global/product';
import {
    QuoteDeliveryOption,
    QuoteSummary,
    QuoteSummaryState,
    TransactionSummary,
    transformTransactionSummary,
} from '@toucan/client/global/transaction';

import { QUOTE_DECLINED_REASONS } from '@toucan/client/core/quote';

import { ConfirmedQuoteItem } from '../config/types';

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface QuoteSellItem {
    modelId: number;
    condition: string;
}

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface QuoteUnknownSellItem {
    description: string;
    condition: string;
}

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface QuoteNewBuyItem {
    modelId: number;
}
// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface QuoteBuyItem {
    productId: number;
}

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface UpdateQuoteSellItem {
    id: string | number;
    modelId: number;
    condition: ProductConditionValue;
    declinedReason?: QUOTE_DECLINED_REASONS;
}

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface UpdateQuoteBuyItem {
    id: string;
    productId: number;
    declinedReason?: QUOTE_DECLINED_REASONS;
}

// Touch and Change
// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export interface QuoteCustomerData {
    firstName: string;
    lastName: string;
    emailAddress: string;
    phoneNumber?: string;
    receiveOffersViaEmail: boolean;
    receiveOffersViaText: boolean;
}

export type QuoteData = {
    customer: QuoteCustomerData;
    discountCode: string | null;
    goodsInKnown: QuoteSellItem[];
    goodsInUnknown: QuoteUnknownSellItem[];
    goodsOutNew: QuoteNewBuyItem[];
    goodsOut: QuoteBuyItem[];
};

export enum QuoteCreationErrorCode {
    UPDATE_FAILED = '010',
}

export async function createQuote(
    data: QuoteData,
    market: MarketCode,
    csrfToken: string,
    turnstileToken?: string,
    clickRef?: string
): Promise<QuoteSummary> {
    const config: AxiosRequestConfig = {
        headers: {
            'X-Market': market.toLowerCase(),
            'CSRF-Token': csrfToken,
            ...(turnstileToken && { 'Turnstile-Token': turnstileToken }),
        },
    };

    const body = {
        ...data,
        ...(clickRef && { clickRef }),
    };

    const response = await client.post('/quote/v1/quote/', body, config);
    return response.data;
}

/**
 * Get quote by transaction ID
 *
 * @param transactionId
 * @param market
 */
export async function getQuoteSummaryByTransactionId(
    transactionId: TransactionSummary['id'],
    market: MarketCode
): Promise<QuoteSummary> {
    const config: AxiosRequestConfig = {
        headers: {
            'X-Market': market.toLowerCase(),
        },
    };

    const response = await client.get(
        `/public-api/quote/v1/transaction/${transactionId}/quote/`,
        config
    );

    return {
        ...response.data,
        transaction: transformTransactionSummary(response.data.transaction),
    };
}

export async function addSellItemToQuote(
    quoteId: string,
    sellItem: ConfirmedQuoteItem,
    market: MarketCode,
    csrfToken: string
) {
    const config: AxiosRequestConfig = {
        headers: {
            'X-Market': market.toLowerCase(),
            'CSRF-Token': csrfToken,
        },
    };

    const data = {
        modelId: sellItem.data?.selectedItem?.id,
        condition: sellItem.data?.condition,
    };

    const response = await client.post(
        `/quote/v1/quote/${quoteId}/good-in/`,
        data,
        config
    );

    return response.data;
}

export async function updateSellItemOnQuote(
    quoteId: string,
    sellItem: UpdateQuoteSellItem,
    declinedReason: string = QUOTE_DECLINED_REASONS.NOT_DECLINED,
    market: MarketCode,
    csrfToken: string
) {
    const config: AxiosRequestConfig = {
        headers: {
            'X-Market': market.toLowerCase(),
            'CSRF-Token': csrfToken,
        },
    };

    const data = {
        modelId: sellItem.modelId,
        condition: sellItem.condition,
        declinedReason,
    };

    const response = await client.post(
        `/quote/v1/quote/${quoteId}/good-in/${sellItem.id}/`,
        data,
        config
    );

    return response.data;
}

export async function addBuyItemToQuote(
    quoteId: string,
    productId: number,
    market: MarketCode,
    csrfToken: string
) {
    const config: AxiosRequestConfig = {
        headers: {
            'X-Market': market.toLowerCase(),
            'CSRF-Token': csrfToken,
        },
    };

    const response = await client.post(
        `/quote/v1/quote/${quoteId}/good-out/`,
        { productId },
        config
    );

    return response.data;
}

export async function updateBuyItemOnQuote(
    quoteId: string,
    buyItem: UpdateQuoteBuyItem,
    declinedReason: string = QUOTE_DECLINED_REASONS.NOT_DECLINED,
    market: MarketCode,
    csrfToken: string
) {
    const config: AxiosRequestConfig = {
        headers: {
            'X-Market': market.toLowerCase(),
            'CSRF-Token': csrfToken,
        },
    };

    const data = {
        productId: buyItem.productId,
        declinedReason,
    };

    const response = await client.post(
        `/quote/v1/quote/${quoteId}/good-out/${buyItem.id}/`,
        data,
        config
    );

    return response.data;
}

type BulkUpdateParams = {
    buyItemsForUpdate: UpdateQuoteBuyItem[];
    buyItemsToAdd: number[];
    sellItemsForUpdate: UpdateQuoteSellItem[];
    sellItemsToAdd: ConfirmedQuoteItem[];
};

export async function bulkChangesToQuote(
    quoteId: string,
    bulkUpdates: BulkUpdateParams,
    market: MarketCode,
    csrfToken: string
) {
    const config: AxiosRequestConfig = {
        headers: {
            'X-Market': market.toLowerCase(),
            'CSRF-Token': csrfToken,
        },
    };
    const defaultDeclinedReason = QUOTE_DECLINED_REASONS.NOT_DECLINED;

    const {
        buyItemsForUpdate,
        buyItemsToAdd,
        sellItemsForUpdate,
        sellItemsToAdd,
    } = bulkUpdates;

    const data = {
        goods_in_known: [
            ...sellItemsForUpdate.map((item) => ({
                declinedReason: defaultDeclinedReason,
                ...item,
            })),
            ...sellItemsToAdd.map((item) => ({
                modelId: item.data?.selectedItem?.id,
                condition: item.data?.condition,
            })),
        ],
        goods_in_unknown: [], // Required by API
        goods_out_new: [], // Required by API
        goods_out_used: [
            ...buyItemsForUpdate.map((item) => ({
                declinedReason: defaultDeclinedReason,
                ...item,
            })),
            ...buyItemsToAdd.map((itemId) => ({
                product_id: itemId,
            })),
        ],
    };

    const response = await client.post(
        `/quote/v1/quote/${quoteId}/goods/`,
        data,
        config
    );

    return response.data;
}

export async function syncQuoteToTransaction(
    quoteId: string,
    market: MarketCode,
    csrfToken: string
) {
    const config: AxiosRequestConfig = {
        headers: {
            'X-Market': market.toLowerCase(),
            'CSRF-Token': csrfToken,
        },
    };

    const response = await client.post(
        `/quote/v1/quote/${quoteId}/sync/`,
        {},
        config
    );

    return response.data;
}

/**
 * Update quote customer information
 *
 * @param id
 * @param address
 */
export async function updateQuoteCustomerInformation(
    id: QuoteSummary['id'],
    options: QuoteCustomerData,
    market: MarketCode,
    csrfToken: string
): Promise<any> {
    const config: AxiosRequestConfig = {
        headers: {
            'X-Market': market.toLowerCase(),
            'CSRF-Token': csrfToken,
        },
    };

    const response = await client.post(
        `/quote/v1/quote/${id}/customer_info/`,
        pick(options, [
            'phoneNumber',
            'receiveOffersViaEmail',
            'receiveOffersViaText',
        ]),
        config
    );

    return response.data;
}

/**
 * Update quote delivery option by ID
 *
 * @param id
 * @param option
 */
export async function updateQuoteDeliveryOption(
    id: QuoteSummary['id'],
    options: QuoteDeliveryOption,
    market: MarketCode,
    csrfToken: string
): Promise<any> {
    const config: AxiosRequestConfig = {
        headers: {
            'X-Market': market.toLowerCase(),
            'CSRF-Token': csrfToken,
        },
    };

    const response = await client.post(
        `/quote/v1/quote/${id}/delivery_option/`,
        options,
        config
    );

    return response.data;
}

export async function getQuote(
    quoteId: string,
    market: MarketCode
): Promise<QuoteSummary> {
    const config: AxiosRequestConfig = {
        headers: {
            'X-Market': market.toLowerCase(),
        },
    };

    const response = await client.get(`/quote/v1/quote/${quoteId}/`, config);

    return response.data;
}

export enum PollQuoteStateError {
    QUOTE_STILL_PROCESSING = 'Quote still processing',
    UNKNOWN = 'unknown',
}

export async function pollQuoteState(
    quoteId: string,
    market: MarketCode,
    retries: number = 5
): Promise<QuoteSummary> {
    const run = async () => {
        const quote = await getQuote(quoteId, market);

        if (
            quote.state !== QuoteSummaryState.PROCESSING &&
            quote.state !== QuoteSummaryState.BLOCKED
        ) {
            return quote;
        }

        throw new Error(PollQuoteStateError.QUOTE_STILL_PROCESSING);
    };

    return pRetry(run, {
        retries,
    }).catch((error) => {
        // Can't throw a NamedException error inside pRetry so doing here instead
        if (error.message === PollQuoteStateError.QUOTE_STILL_PROCESSING) {
            throw new NamedException(
                PollQuoteStateError.QUOTE_STILL_PROCESSING,
                PollQuoteStateError.QUOTE_STILL_PROCESSING
            );
        }

        throw new NamedException(
            PollQuoteStateError.UNKNOWN,
            'Unknown error getting transaction id'
        );
    });
}

export async function getTransaction(
    transactionId: string,
    market: MarketCode
): Promise<TransactionSummary> {
    const config: AxiosRequestConfig = {
        headers: {
            'X-Market': market.toLowerCase(),
        },
    };

    const response = await client.get(
        `/transaction-service/${transactionId}/summary/`,
        config
    );

    return transformTransactionSummary(response.data);
}

export async function getModelPurchasePrice(
    modelId: string | number,
    condition: ProductConditionValue,
    market: MarketCode
): Promise<{ condition: string; purchaseValue: number }> {
    const legacyCondition = productConditionEnumToLegacyEnum(condition);

    const { data } = await client.get(
        `/public-api/v1/models/purchase-price/${modelId}/${legacyCondition}/`,
        {
            headers: {
                'X-Market': market.toLowerCase(),
            },
        }
    );

    // We deal with cents in transactions so we need convert it
    data.purchaseValue = decimalToCent(data.purchaseValue);

    return data;
}

export async function updatePromoCodeOnQuote(
    quoteId: string,
    code: string,
    market: MarketCode,
    csrfToken: string
) {
    const config: AxiosRequestConfig = {
        headers: {
            'X-Market': market.toLowerCase(),
            'CSRF-Token': csrfToken,
        },
    };

    const response = await client.post(
        `/quote/v1/quote/${quoteId}/promo-code/`,
        { promo_code: code },
        config
    );

    return response.data;
}
